/* ============================================================================
 * Crofters — theme scripts
 *
 * All initializers are idempotent: they guard against re-binding so they can be
 * safely re-run on `shopify:section:load` (theme editor) without leaking
 * listeners or stacking duplicate animations.
 * ========================================================================== */

/* ---------------------------------------------------------------------------
 * Dual CTA section
 * ------------------------------------------------------------------------- */

/**
 * Initialize the dual CTA Swiper carousel.
 * 3 or fewer cards stay static; 4+ becomes a slider.
 */
function initDualCtaSwiper() {
    document.querySelectorAll('.dual-cta-swiper').forEach((swiperContainer) => {
        if (swiperContainer.swiper) return; // already initialized

        const section = swiperContainer.closest('.dual-cta-swiper-row');
        const slides = swiperContainer.querySelectorAll('.swiper-slide');
        const nextBtn = section ? section.querySelector('.swiper-button-next') : null;
        const prevBtn = section ? section.querySelector('.swiper-button-prev') : null;

        if (slides.length > 3 && typeof Swiper !== 'undefined') {
            new Swiper(swiperContainer, {
                slidesPerView: 1,
                spaceBetween: 30,
                navigation: { nextEl: nextBtn, prevEl: prevBtn },
                breakpoints: {
                    320: { slidesPerView: 1, spaceBetween: 20 },
                    768: { slidesPerView: 2, spaceBetween: 30 },
                    1024: { slidesPerView: 3, spaceBetween: 40 },
                },
            });
        } else {
            if (nextBtn) nextBtn.classList.add('hidden');
            if (prevBtn) prevBtn.classList.add('hidden');
            swiperContainer.classList.add('static-layout');
        }
    });
}

/**
 * Add a ripple effect to CTA buttons on click.
 * Requires the `.ripple` / `@keyframes ripple-animation` CSS in your stylesheet.
 */
function initCtaButtons() {
    document.querySelectorAll('.dual-cta-btn .site-btn').forEach((btn) => {
        if (btn.dataset.rippleBound === 'true') return;

        btn.addEventListener('click', (e) => {
            const rect = btn.getBoundingClientRect();
            const size = Math.max(rect.width, rect.height);

            const ripple = document.createElement('span');
            ripple.className = 'ripple';
            ripple.style.width = ripple.style.height = `${size}px`;
            ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
            ripple.style.top = `${e.clientY - rect.top - size / 2}px`;

            btn.appendChild(ripple);
            setTimeout(() => ripple.remove(), 600);
        });

        btn.dataset.rippleBound = 'true';
    });
}

/**
 * Responsive max-width for the dual CTA swiper. The resize listener is bound
 * only once, no matter how many times this runs.
 */
let dualCtaResizeBound = false;

function updateDualCtaLayout() {
    const dualCtaSwiper = document.querySelector('.dual-cta-swiper');
    if (!dualCtaSwiper) return;
    dualCtaSwiper.style.maxWidth = window.innerWidth < 768 ? '100%' : 'none';
}

function handleResponsiveBehavior() {
    updateDualCtaLayout();
    if (!dualCtaResizeBound) {
        window.addEventListener('resize', updateDualCtaLayout);
        dualCtaResizeBound = true;
    }
}

function initDualCtaSection() {
    initDualCtaSwiper();
    // initImageHoverEffects();
    // initCtaButtons();
    handleResponsiveBehavior();
}

/* ---------------------------------------------------------------------------
 * Theme video — paw animation (plays once when fully in view)
 * ------------------------------------------------------------------------- */

function initThemeVideoPaw() {
    const block = document.querySelector('.theme-video-paw-animation');
    if (!block || block.dataset.pawBound === 'true') return;
    block.dataset.pawBound = 'true';

    const paw = block.querySelector('.theme-video__deco--paw');
    const blueberry = block.querySelector('.theme-video__deco[data-index="4"]');

    if (paw) {
        paw.style.pointerEvents = 'auto';
    }

    let animationTimer = null;
    let sequenceTimeout = null;

    function startAnimationCycle() {
        clearTimeout(sequenceTimeout);
        block.classList.remove('play-sequence', 'fruit-hidden', 'is-clicked');

        // Force reflow
        void block.offsetWidth;

        block.classList.add('play-sequence');
    }

    if (paw) {
        paw.addEventListener('animationend', (e) => {
            if (e.animationName === 'paw-sequence' || e.animationName === 'paw-click-sequence') {
                block.classList.remove('play-sequence', 'is-clicked');
                block.classList.add('fruit-hidden');

                sequenceTimeout = setTimeout(() => {
                    startAnimationCycle();
                }, 3000);
            }
        });
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                const maxRatio = Math.min(1, window.innerHeight / entry.boundingClientRect.height);
                const isFullyVisible = entry.isIntersecting && entry.intersectionRatio >= maxRatio * 0.95;

                if (isFullyVisible && block.dataset.animationTriggered !== 'true') {
                    if (!animationTimer) {
                        animationTimer = setTimeout(() => {
                            block.dataset.animationTriggered = 'true';
                            startAnimationCycle();
                            animationTimer = null;
                        }, 100);
                    }
                } else if (!isFullyVisible && animationTimer && block.dataset.animationTriggered !== 'true') {
                    clearTimeout(animationTimer);
                    animationTimer = null;
                }
            });
        },
        { threshold: Array.from({ length: 21 }, (_, i) => i / 20) }
    );

    observer.observe(block);

    const isDesktop = window.matchMedia("(min-width: 992px)").matches;
    // const isDesktop = window.matchMedia("(min-width: 1441px)").matches;

    if (paw && blueberry && isDesktop) {
        let isStoppedByClick = false;
        let clickResumeTimeout = null;

        paw.addEventListener('mouseenter', () => {
            if (!isStoppedByClick && !block.classList.contains('fruit-hidden')) {
                block.classList.add('is-paused');
            }
        });

        paw.addEventListener('mouseleave', () => {
            if (!isStoppedByClick) {
                block.classList.remove('is-paused');
            }
        });

        paw.addEventListener('click', () => {
            // Prevent interacting while the fruit is fully hidden (waiting for 5s timer)
            if (block.classList.contains('fruit-hidden')) return;

            isStoppedByClick = !isStoppedByClick;

            if (isStoppedByClick) {
                block.classList.add('is-paused', 'is-stopped');
                paw.style.display = 'none';

                // Automatically restart from the beginning after 3 seconds without a click
                clearTimeout(clickResumeTimeout);
                clickResumeTimeout = setTimeout(() => {
                    if (isStoppedByClick) {
                        isStoppedByClick = false;
                        block.classList.remove('is-paused', 'is-stopped');
                        paw.style.display = '';
                        startAnimationCycle();
                    }
                }, 3000);
            } else {
                block.classList.remove('is-paused', 'is-stopped');
                paw.style.display = '';
                clearTimeout(clickResumeTimeout);
            }
        });
    }
}

// initThemeVideoPaw();

/* ---------------------------------------------------------------------------
 * Bootstrap — run once on load, and again whenever a section is (re)loaded.
 * ------------------------------------------------------------------------- */

function initTheme() {
    initDualCtaSection();
    initThemeVideoPaw();
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initTheme);
} else {
    initTheme();
}

document.addEventListener('shopify:section:load', initTheme);

